// /teachers-wanted — founding-teacher collection page.
// One clear offer: free Pro forever for teachers who use Lectura with a class and share their story.
// Nicole can DM/email this URL to any teacher she meets.
import { useState } from "react";
import { SEO } from "@/components/SEO";
import { MarketingNav } from "@/components/MarketingNav";
import { MarketingFooter } from "@/components/MarketingFooter";
import { Link } from "react-router-dom";
import { GraduationCap, Gift, Check, Mail, Sparkles, ShieldCheck } from "lucide-react";
import { submitTeacherApplication } from "@/lib/api";
import { toast } from "sonner";

const PERKS = [
  "Free Pro forever — for you and every student in your class",
  "Priority queue on all AI generations",
  "Live Class Game Mode for your quizzes",
  "Direct line to the founder — feature requests jump the queue",
  "Case-study page on getlectura.app (optional)",
];

export default function TeachersWantedPage() {
  const [form, setForm] = useState({
    name: "", email: "", school: "", grade: "", subject: "", story: "", agree: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const canSubmit = form.name.trim() && form.email.includes("@") && form.school.trim() && form.agree;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    try {
      await submitTeacherApplication(form);
      setSubmitted(true);
      toast.success("Application received — Nicole will reply within 24 hours.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't submit right now. Please email hello@getlectura.app.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEO
        title="Founding Teachers Wanted — Free Lectura Pro for Life"
        description="Apply to be a Lectura founding teacher. Free Pro forever for you and your class in exchange for using it and sharing your story. K-12 or homeschool."
        canonical="https://getlectura.app/teachers-wanted"
      />
      <MarketingNav />
      <main>
        <section className="border-b border-border/60">
          <div className="mx-auto max-w-3xl px-4 sm:px-6 py-14 text-center">
            <div className="inline-flex items-center gap-2 rounded-full bg-accent/15 text-accent px-3 py-1 text-xs font-bold uppercase tracking-widest">
              <GraduationCap className="h-3.5 w-3.5" /> Founding teachers wanted
            </div>
            <h1 className="mt-4 font-serif text-4xl sm:text-5xl font-bold">
              Free Lectura Pro. Forever. For you and your class.
            </h1>
            <p className="mt-4 text-muted-foreground max-w-2xl mx-auto">
              We're looking for 10 K-12 or homeschool teachers to use Lectura with a real class this semester.
              You get free Pro forever. We get a real case study and one honest quote for the site. That's the deal — no strings.
            </p>
          </div>
        </section>

        {/* Offer + form side by side */}
        <section className="mx-auto max-w-5xl px-4 sm:px-6 py-14 grid gap-8 lg:grid-cols-2 items-start">
          {/* Offer column */}
          <div className="rounded-3xl border-2 border-accent/40 bg-accent/5 p-6 sm:p-8">
            <span className="inline-grid place-items-center h-12 w-12 rounded-2xl bg-accent text-accent-foreground">
              <Gift className="h-6 w-6" />
            </span>
            <h2 className="mt-4 font-serif text-2xl font-bold">What you get</h2>
            <ul className="mt-4 space-y-2.5 text-sm">
              {PERKS.map((p, i) => (
                <li key={i} className="flex items-start gap-2">
                  <Check className="h-4 w-4 text-accent mt-0.5 shrink-0" />
                  <span>{p}</span>
                </li>
              ))}
            </ul>

            <h3 className="mt-6 font-serif text-lg font-bold">What we ask</h3>
            <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
              <li>Use Lectura with a real class for at least 3 chapters this semester.</li>
              <li>Send us one honest paragraph about what worked and what didn't.</li>
              <li>Let us quote you (first name + last initial + role) on the site if the results are real.</li>
            </ul>

            <div className="mt-6 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
              <span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5" /> FERPA-Ready</span>
              <span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5" /> COPPA-Compliant</span>
              <span className="inline-flex items-center gap-1.5">No student data training</span>
            </div>
          </div>

          {/* Form column */}
          <div className="rounded-3xl border border-border bg-card p-6 sm:p-8">
            {submitted ? (
              <div className="text-center py-6">
                <span className="inline-grid place-items-center h-14 w-14 rounded-2xl bg-emerald-100 text-emerald-700">
                  <Check className="h-7 w-7" />
                </span>
                <h2 className="mt-4 font-serif text-2xl font-bold">You're in the queue.</h2>
                <p className="mt-2 text-sm text-muted-foreground">
                  Nicole will personally reply within 24 hours with your free Pro upgrade instructions and next steps.
                </p>
                <Link to="/" className="mt-6 inline-flex rounded-full bg-primary text-primary-foreground px-5 py-2.5 text-sm font-bold">
                  Try Lectura while you wait
                </Link>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="teachers-wanted-form" className="space-y-4">
                <h2 className="font-serif text-2xl font-bold">Apply in 30 seconds</h2>

                <div>
                  <label htmlFor="tw-name" className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Your name</label>
                  <input
                    id="tw-name" data-testid="tw-name" type="text" required
                    value={form.name} onChange={set("name")}
                    className="mt-1 w-full rounded-xl border border-border bg-background px-4 py-2.5 text-sm"
                    placeholder="Sam Rivera"
                  />
                </div>

                <div>
                  <label htmlFor="tw-email" className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Work email</label>
                  <input
                    id="tw-email" data-testid="tw-email" type="email" required
                    value={form.email} onChange={set("email")}
                    className="mt-1 w-full rounded-xl border border-border bg-background px-4 py-2.5 text-sm"
                    placeholder="sam@lincolnhigh.k12.us"
                  />
                </div>

                <div>
                  <label htmlFor="tw-school" className="text-xs font-bold uppercase tracking-widest text-muted-foreground">School or district</label>
                  <input
                    id="tw-school" data-testid="tw-school" type="text" required
                    value={form.school} onChange={set("school")}
                    className="mt-1 w-full rounded-xl border border-border bg-background px-4 py-2.5 text-sm"
                    placeholder="Lincoln High · Austin, TX (or Homeschool)"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label htmlFor="tw-grade" className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Grade</label>
                    <input
                      id="tw-grade" data-testid="tw-grade" type="text"
                      value={form.grade} onChange={set("grade")}
                      className="mt-1 w-full rounded-xl border border-border bg-background px-4 py-2.5 text-sm"
                      placeholder="9-12"
                    />
                  </div>
                  <div>
                    <label htmlFor="tw-subject" className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Subject</label>
                    <input
                      id="tw-subject" data-testid="tw-subject" type="text"
                      value={form.subject} onChange={set("subject")}
                      className="mt-1 w-full rounded-xl border border-border bg-background px-4 py-2.5 text-sm"
                      placeholder="AP Biology"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="tw-story" className="text-xs font-bold uppercase tracking-widest text-muted-foreground">How would you use it?</label>
                  <textarea
                    id="tw-story" data-testid="tw-story"
                    value={form.story} onChange={set("story")}
                    rows={3}
                    className="mt-1 w-full rounded-xl border border-border bg-background px-4 py-2.5 text-sm resize-none"
                    placeholder="e.g. I'd generate Chapter 5 (Cell Structure) as a Lectura lecture and quiz for my flipped-classroom homework."
                  />
                </div>

                <label className="flex items-start gap-2 text-xs text-muted-foreground">
                  <input
                    type="checkbox" data-testid="tw-agree" required
                    checked={form.agree} onChange={set("agree")}
                    className="mt-0.5"
                  />
                  <span>
                    I understand this is a founding-teacher program. I'll try Lectura with a class and share one honest paragraph of feedback. I can be quoted (first name + last initial) if I opt in.
                  </span>
                </label>

                <button
                  data-testid="tw-submit"
                  type="submit"
                  disabled={!canSubmit || submitting}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground px-6 py-3 font-bold hover:opacity-90 transition disabled:opacity-50"
                >
                  <Sparkles className="h-4 w-4" />
                  {submitting ? "Sending…" : "Apply — takes 30 seconds"}
                </button>

                <p className="text-xs text-muted-foreground text-center">
                  Or email <a className="text-accent underline" href="mailto:hello@getlectura.app?subject=Founding%20teacher%20application">hello@getlectura.app</a> directly.
                </p>
              </form>
            )}
          </div>
        </section>

        <section className="border-t border-border/60 bg-primary text-primary-foreground">
          <div className="mx-auto max-w-3xl px-4 sm:px-6 py-14 text-center">
            <h2 className="font-serif text-3xl font-bold inline-flex items-center gap-3 justify-center flex-wrap">
              <Mail className="h-6 w-6 text-accent" /> Not a teacher? Share this link.
            </h2>
            <p className="mt-3 text-primary-foreground/80 max-w-xl mx-auto">
              Send <span className="font-mono text-accent">getlectura.app/teachers-wanted</span> to one teacher you know. If they join, we'll credit you 30 days of Pro too.
            </p>
          </div>
        </section>
      </main>
      <MarketingFooter />
    </div>
  );
}
